import { useMemo } from 'react';
import { courseRegistry } from '../courses/registry';
import type { CourseConfig } from '../courses/types';
import { useCourse } from '../contexts/CourseContext';
import { DEFAULT_COURSE_CODE } from '../constants/config';

const defaultPreferenceCategories: NonNullable<CourseConfig['preferences']>['categories'] = [
  {
    title: 'Clear',
    options: [
      'Avoid long words or complex phrases',
      'Keep sentences under 15 words',
      'Avoid using sayings or figures of speech',
    ],
  },
  {
    title: 'Structured',
    options: [
      'Use dot points instead of paragraphs',
      'Add headings for each section',
      'Number steps in a process',
    ],
  },
  {
    title: 'Detailed',
    options: [
      'Include real-world examples',
      'Provide deeper explanations',
      'Use analogies to explain ideas',
    ],
  },
];

export function useCourseConfig(): CourseConfig {
  const { currentCourse } = useCourse();

  return useMemo(() => {
    const code = currentCourse?.code || DEFAULT_COURSE_CODE;
    const reg = courseRegistry[code] ?? courseRegistry[DEFAULT_COURSE_CODE];

    // Merge with sensible defaults without mutating the registry
    const merged: CourseConfig = {
      code: reg.code,
      flow: reg.flow,
      landing: {
        title: reg.landing?.title,
        subtitle: reg.landing?.subtitle,
        videoId: reg.landing?.videoId ?? 'g_16IsJRCOE',
        headings: reg.landing?.headings,
        copy: reg.landing?.copy,
      },
      preferences: {
        categories: reg.preferences?.categories ?? defaultPreferenceCategories,
      },
      chat: {
        exitAfter: reg.chat?.exitAfter ?? 5,
        includeActivityResponses: reg.chat?.includeActivityResponses,
        autoStartWithActivity: reg.chat?.autoStartWithActivity,
        requireExitResponse: reg.chat?.requireExitResponse,
      },
      activities: reg.activities, // Include activities config
    };

    return merged;
  }, [currentCourse?.code]);
}
